import React, { useLayoutEffect, useRef, useState } from 'react';
import type { Point, Projet, CategorieMarqueur } from '../../types/map';
import './ResultCard.css';
import { resolveAssetUrl } from '../../config/api';
import { resolveTextOnColor } from './textOnColor';

export interface ResultCardProps {
  projet: Projet;
  categorie?: CategorieMarqueur;
  /** Clic sur le picto marqueur → recentre la carte (SPEC §5.4). */
  onPictoClick: (projet: Projet, point?: Point) => void;
  /** Clic « Afficher les détails » → ouvre le détail selon le mode de la catégorie. */
  onDetailsClick: (projet: Projet, point?: Point) => void;
}

/** Retourne l'URL d'image du projet depuis donneesPersonnalisees selon le schéma de la catégorie. */
function resolveImageUrl(
  donneesPersonnalisees: Record<string, unknown> | undefined,
  categorie?: CategorieMarqueur
): string | null {
  const imageMeta = categorie?.metadonnees?.find((m) => m.type === 'image');
  if (!imageMeta) return null;
  const value = donneesPersonnalisees?.[imageMeta.cle];
  if (typeof value !== 'string' || value.length === 0) return null;
  return resolveAssetUrl(value);
}

/**
 * Carte de résultat (SPEC §5) affichée dans le bottom sheet « Vos résultats ».
 * Une carte représente un PROJET (0..N emplacements) : un sélecteur de site
 * apparaît pour choisir un emplacement précis quand il y en a plusieurs.
 * Présentationnelle : ne filtre pas les données et n'ouvre pas le détail
 * (délègue via onDetailsClick).
 */
export const ResultCard: React.FC<ResultCardProps> = ({
  projet,
  categorie,
  onPictoClick,
  onDetailsClick,
}) => {
  const title = projet.titre || projet.nom || '';
  const points = projet.points || [];
  const hasMultipleSites = points.length > 1;
  const hasPosition = points.length > 0;

  // Site sélectionné dans le sélecteur (multi-emplacements) ; -1 = « tous les sites ».
  const [selectedSiteIndex, setSelectedSiteIndex] = useState<number>(-1);
  const selectedPoint =
    points.length === 1 ? points[0] : selectedSiteIndex >= 0 ? points[selectedSiteIndex] : undefined;

  const imageUrl = resolveImageUrl(projet.donneesPersonnalisees, categorie);

  // Picto : icône/pastille du marqueur (catégorie).
  const isImagePicto = categorie?.typeIcone === 'image' && !!categorie?.imageMarqueur;
  const pictoEmoji = '📍';

  // Détection de débordement pour activer le marquee (SPEC §5.1).
  const titleRef = useRef<HTMLSpanElement>(null);
  const [overflow, setOverflow] = useState(0);
  useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    setOverflow(Math.max(0, el.scrollWidth - el.clientWidth));
  }, [title]);
  const overflowing = overflow > 0;

  // Couleur du haut de carte = couleur de la catégorie (défaut défini en CSS).
  const accentStyle: Record<string, string> = {};
  if (categorie?.couleur) {
    accentStyle['--card-accent'] = categorie.couleur;
    const textOnAccent = resolveTextOnColor(categorie.couleur);
    if (textOnAccent) accentStyle['--card-accent-text'] = textOnAccent;
  }

  return (
    <article className="result-card" style={accentStyle as React.CSSProperties}>
      <h3
        className={`result-card__title${overflowing ? ' is-overflowing' : ''}`}
        title={title}
      >
        <span
          className="result-card__title-text"
          ref={titleRef}
          aria-label={title}
          style={{ ['--marquee-distance' as string]: `${overflow}px` }}
        >
          {title}
        </span>
      </h3>

      {/* Picto marqueur — masqué si le projet n'a aucun emplacement positionné (§5.1/§5.3) */}
      {hasPosition && (
        <button
          type="button"
          className="result-card__picto"
          aria-label="Centrer la carte sur ce projet"
          onClick={() => onPictoClick(projet, selectedPoint)}
        >
          {isImagePicto ? (
            <img src={resolveAssetUrl(categorie!.imageMarqueur)} alt="" className="result-card__picto-img" />
          ) : (
            <span aria-hidden="true">{pictoEmoji}</span>
          )}
        </button>
      )}

      <div className="result-card__media">
        {imageUrl ? (
          <img
            className="result-card__image"
            src={imageUrl}
            alt=""
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="result-card__image result-card__image--fallback" aria-hidden="true" />
        )}
      </div>

      {/* Sélecteur de site — projet à emplacements multiples uniquement (§5.4). */}
      {hasMultipleSites && (
        <label className="result-card__site-select">
          <span className="result-card__sr-label">Choisir un emplacement</span>
          <select
            value={selectedSiteIndex}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setSelectedSiteIndex(parseInt(e.target.value, 10))}
          >
            <option value={-1}>Tous les sites ({points.length})</option>
            {points.map((pt, idx) => (
              <option key={pt.id} value={idx}>
                {pt.nom || `Emplacement ${idx + 1}`}
              </option>
            ))}
          </select>
        </label>
      )}

      <button
        type="button"
        className="result-card__details"
        onClick={() => onDetailsClick(projet, selectedPoint)}
      >
        Afficher les détails
      </button>
    </article>
  );
};

export default ResultCard;
